import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import MobileNavbar from '../../components/MobileNavbar/MobileNavbar.component'
import useBreakpoint from '../../hooks/useBreakpoint'
import { loginRoute } from '../Login/route.const'
import { Footer } from 'parkdepot-shared/components/Footer/Footer'
import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_KIND, BUTTON_SIZE } from 'parkdepot-shared/components/Buttons/types'
import { PublicHeader } from 'parkdepot-shared/components/PublicHeader/PublicHeader'
import { LanguageSelectOptions } from 'parkdepot-shared/components/Dropdown'
import { useTranslation } from 'react-i18next'
import { useGetUser } from '../../hooks/useAuth'
import { guidelinesRoute } from '../Guidelines/route'
import { helpRoute } from '../Help/route.const'
import { homeRoute } from '../Home/route.const'
import { profileRoute } from '../Profile/route.const'
import { searchRoute } from '../Search/route'
import useLanguage from '../../hooks/useLanguage.hook'
import {
    AppFrameWrapper,
    BodyWrapper,
    HeaderButtonStyleOverrides,
    HeaderCommonButtonStylesOverrides
} from './AppFrame.style'
import { useStyletron } from 'parkdepot-shared/theme'
import { ConsentContext } from '@context/ConsentProvider/ConsentProvider'
import PaymentAdvertisement from '@components/PaymentAdvertisement/PaymentAdvertisement.component'
import { LOCAL_STORAGE_setSignupBannerShown } from '@localStorage/signupBannerShown.localStorage'
import { useSendGTMEvent } from '@hooks/useSendGTMEvent.hook'
import { allLandingPageRoutes } from '@views/LandingPages/routes'
import { guestRoute } from '@views/Guest'
import { getDomainExtension } from '@utils/getDomainExtension'

const Languages = LanguageSelectOptions.filter((lang) => {
    return lang.id === 'de' || lang.id === 'en' || lang.id === 'fr' || lang.id === 'it'
})

interface AppFrameProps {
    children: ReactNode
    config: { hasNavbar: boolean }
}

const AppFrame = ({ children, config: { hasNavbar } }: AppFrameProps) => {
    const { pathname } = useLocation()
    const breakpoint = useBreakpoint()
    const history = useHistory()
    const { t } = useTranslation()
    const [lng, changeLanguage] = useLanguage()
    const { user } = useGetUser()
    const [, theme] = useStyletron()
    const [currentRoute, setCurrentRoute] = useState<string | undefined>()
    const isGuestUiFlow = pathname.includes(guestRoute())

    if (user) {
        LOCAL_STORAGE_setSignupBannerShown(true)
    }
    const permissions = useContext(ConsentContext)
    const { sendPageViewEvent } = useSendGTMEvent()

    const language = lng.split('-')[0] ?? lng
    const isMobile = breakpoint <= 1

    const footerLinks = [
        {
            label: t('footer.help'),
            to: helpRoute()
        },
        {
            label: t('guidelines.title'),
            to: guidelinesRoute()
        },
        {
            label: t('footer.cookieSettings'),
            clicked: () => {
                if (window.UC_UI) {
                    window.UC_UI.showSecondLayer()
                } else {
                    console.error('Usercentrics is not loaded yet.')
                }
            }
        }
    ]

    const footerMobileLinks = [
        {
            label: t('footer.help'),
            to: helpRoute()
        },
        {
            label: t('guidelines.title'),
            to: guidelinesRoute()
        },
        {
            label: t('footer.cookieSettings'),
            clicked: () => {
                if (window.UC_UI) {
                    window.UC_UI.showSecondLayer()
                } else {
                    console.error('Usercentrics is not loaded yet.')
                }
            }
        }
    ]

    const additionalLinks = useMemo(() => {
        return allLandingPageRoutes().map((route) => ({
            label: t(`landingPages.${route.key}.name`),
            to: route.originalPath
        }))
    }, [t])

    useEffect(() => {
        sendPageViewEvent()

        // scroll to top on every page change; kind of a hack but works
        setCurrentRoute((curr) => {
            if (curr === pathname) return curr
            window.scrollTo(0, 0)
            return pathname
        })
    }, [pathname, isMobile, permissions, sendPageViewEvent])

    useEffect(() => {
        document.documentElement.lang = lng
    }, [lng])

    const onLogoClick = () => {
        history.push(homeRoute())
    }

    const handleHomeClick = () => {
        history.push(homeRoute())
    }

    const handleBookingClick = () => {
        history.push(searchRoute())
    }

    const onMyProfileClick = () => {
        history.push(profileRoute())
    }

    const loginInClick = () => {
        history.push(`${loginRoute()}`)
    }

    const onLanguageChange = (id: string) => {
        const domain = getDomainExtension()
        if (domain === 'ch' && ['de', 'it'].includes(id)) {
            changeLanguage(`${id}-CH`)
        } else {
            changeLanguage(id)
        }
    }

    return (
        <AppFrameWrapper>
            <PublicHeader
                onLogoClick={onLogoClick}
                actions={
                    !isMobile
                        ? () => [
                              <Button
                                  id="home-header-button"
                                  key="home"
                                  $as="a"
                                  onClick={(e) => {
                                      e.preventDefault()
                                      handleHomeClick()
                                  }}
                                  href={homeRoute()}
                                  $size={BUTTON_SIZE.medium}
                                  $kind={pathname === homeRoute() ? BUTTON_KIND.primary : BUTTON_KIND.secondary}
                                  startEnhancer={<i className="wemolo-wemolo-icon" aria-hidden="true" />}
                                  overrides={{
                                      BaseButton: {
                                          style: {
                                              ...HeaderCommonButtonStylesOverrides.style,
                                              ...(!(pathname === homeRoute()) ? HeaderButtonStyleOverrides.style : {})
                                          }
                                      },
                                      StartEnhancer: {
                                          style: { fontSize: theme.sizing.scale800 }
                                      }
                                  }}
                              >
                                  {t('header.home')}
                              </Button>,
                              <Button
                                  id="booking-header-button"
                                  key="booking"
                                  $size={BUTTON_SIZE.medium}
                                  $kind={/book/.test(pathname) ? BUTTON_KIND.primary : BUTTON_KIND.secondary}
                                  onClick={(e) => {
                                      e.preventDefault()
                                      handleBookingClick()
                                  }}
                                  $as="a"
                                  href={searchRoute()}
                                  startEnhancer={<i className="wemolo-parking-ok" />}
                                  overrides={{
                                      BaseButton: {
                                          style: {
                                              ...(!/book/.test(pathname) ? HeaderButtonStyleOverrides.style : {}),
                                              ...HeaderCommonButtonStylesOverrides.style
                                          }
                                      },
                                      StartEnhancer: {
                                          style: { fontSize: theme.sizing.scale800 }
                                      }
                                  }}
                              >
                                  {t('header.reservation')}
                              </Button>,
                              <Button
                                  id="login-header-button"
                                  key="login-button"
                                  onClick={(e) => {
                                      e.preventDefault()
                                      if (user) {
                                          onMyProfileClick()
                                      } else {
                                          loginInClick()
                                      }
                                  }}
                                  $size={BUTTON_SIZE.medium}
                                  $as="a"
                                  href={user ? profileRoute() : loginRoute()}
                                  $kind={
                                      /profile/.test(pathname) || /login/.test(pathname) || /register/.test(pathname)
                                          ? BUTTON_KIND.primary
                                          : BUTTON_KIND.secondary
                                  }
                                  startEnhancer={<i className="wemolo-user" aria-hidden="true" />}
                                  overrides={{
                                      BaseButton: {
                                          style: {
                                              ...(!/profile/.test(pathname) &&
                                              !/login/.test(pathname) &&
                                              !/register/.test(pathname)
                                                  ? HeaderButtonStyleOverrides.style
                                                  : {}),
                                              ...HeaderCommonButtonStylesOverrides.style
                                          }
                                      },
                                      StartEnhancer: {
                                          style: { fontSize: theme.sizing.scale800 }
                                      }
                                  }}
                              >
                                  {user ? user.first_name : t('profile.loginText')}
                              </Button>
                          ]
                        : undefined
                }
                hideMobileDrawer
            />

            <BodyWrapper $hasNavbar={hasNavbar} $isMobile={isMobile}>
                {children}
                {!user && permissions.hasDecided && !isGuestUiFlow && <PaymentAdvertisement />}
            </BodyWrapper>

            <Footer
                links={isMobile ? footerMobileLinks : footerLinks}
                selectedLanguage={language}
                languages={Languages}
                onLanguageChange={onLanguageChange}
                additionalLinks={additionalLinks}
            />

            {isMobile && hasNavbar && <MobileNavbar />}
        </AppFrameWrapper>
    )
}

export default AppFrame
